import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const DELIVERY_STATUS_FILTERS = [
  { value: 'all', label: 'All Delivery Statuses' },
  { value: 'active', label: 'Active (Ongoing)' },
  { value: 'academic delivery completed', label: 'Academic Delivery Completed' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'certificate received', label: 'Certificate Received' },
  { value: 'certificate dispatched', label: 'Certificate Dispatched' },
  { value: 'Dropped', label: 'Dropped' },
  { value: 'Transfer to another branch', label: 'Transfer to Another Branch' },
  { value: 'Transfer to another course', label: 'Transfer to Another Course' },
];

export default function StudentsWithoutImagesNotifications() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('active');

  // Basic pagination (backend already supports it); keep simple for now
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 50 });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await axiosInstance.get('/admin/students', {
          params: {
            hasImage: 'false',
            search: search.trim() || undefined,
            deliveryStatus,
            page,
            perPage: 100,
          },
        });

        if (cancelled) return;

        const data = resp.data || {};
        setStudents(Array.isArray(data.students) ? data.students : []);
        if (data.pagination) {
          setPagination(data.pagination);
        } else {
          setPagination({ current_page: 1, last_page: 1, total: data.students?.length || 0, per_page: 100 });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err?.response?.data?.message || 'Failed to load students without profile images.';
          setError(message);
          setStudents([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Light debounce for search
    const timeoutId = setTimeout(load, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [search, deliveryStatus, page]);

  const hasData = students && students.length > 0;

  const summaryText = useMemo(() => {
    if (!pagination || pagination.total === 0) {
      return 'No students without profile images were found for the current filters.';
    }
    return `Showing ${students.length} of ${pagination.total} students without profile images`;
  }, [students.length, pagination]);

  const handleDownloadExcel = async () => {
    if (!students || students.length === 0) {
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const headers = [
        'Student ID',
        'Full Name',
        'DOB',
        'Telephone',
        'Email',
        'Address',
        'NIC Number',
        'Registrations Count',
        'Registrations (Course / Batch / Reg No)',
      ];

      const rows = students.map((student) => {
        const regs = Array.isArray(student.registrations) ? student.registrations : [];
        const registrationsSummary = regs
          .map((reg) => {
            const course = reg.courseShortName || reg.courseName || 'Course';
            const batch = reg.batchName || reg.batchInternalName || 'Batch';
            const regNo = reg.registrationNumber || '';
            return `${course} | ${batch} | ${regNo}`;
          })
          .join('\r\n');

        return [
          student.studentId || '',
          student.fullName || '',
          student.dob || '',
          student.telephone || '',
          student.email || '',
          student.address || '',
          student.nicNumber || '',
          regs.length || 0,
          registrationsSummary,
        ];
      });

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Students Without Images');

      const today = new Date().toISOString().split('T')[0];
      const fileName = `Students_Without_Images_${today}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Failed to download students without images Excel', err);
    }
  };

  return (
    <DashboardLayout title="Students Without Profile Images">
      <div className="space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Students Without Profile Images</h1>
            <p className="mt-1 text-sm text-gray-600">
              List of active students who do not yet have a profile photograph recorded in the system.
            </p>
          </div>
          <div className="space-y-1 text-right">
            <div className="inline-flex items-center rounded-full bg-indigo-50 px-4 py-1.5 text-xs font-medium text-indigo-700">
              <span className="mr-2 uppercase tracking-wide">Students</span>
              <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                {pagination.total || 0}
              </span>
            </div>
            <div className="text-[11px] text-gray-500">{summaryText}</div>
            {hasData && (
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download as Excel
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm">
            Loading students without profile images...
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-4">
                <div className="md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600">Delivery Status</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    value={deliveryStatus}
                    onChange={(e) => {
                      setDeliveryStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    {DELIVERY_STATUS_FILTERS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-600">Search</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search by name, student ID, NIC, phone, course, batch, or registration number"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {hasData ? (
              <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Student
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Contact
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        NIC
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Active Registrations
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Image Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {students.map((student) => {
                      const regs = Array.isArray(student.registrations) ? student.registrations : [];
                      return (
                        <tr key={student.studentId} className="hover:bg-gray-50">
                          <td className="px-4 py-2 align-top">
                            <div className="font-medium text-gray-900">{student.fullName || 'N/A'}</div>
                            <div className="mt-0.5 text-xs text-gray-500">ID: {student.studentId}</div>
                            {student.dob && (
                              <div className="mt-0.5 text-[11px] text-gray-400">DOB: {student.dob}</div>
                            )}
                          </td>
                          <td className="px-4 py-2 align-top text-xs text-gray-700">
                            {student.telephone && <div>{student.telephone}</div>}
                            {student.email && (
                              <div className="mt-0.5 text-[11px] text-gray-500">{student.email}</div>
                            )}
                            {student.address && (
                              <div className="mt-0.5 line-clamp-2 text-[11px] text-gray-400">{student.address}</div>
                            )}
                          </td>
                          <td className="px-4 py-2 align-top text-xs text-gray-700">
                            {student.nicNumber || <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-2 align-top text-xs text-gray-700">
                            {regs.length === 0 ? (
                              <span className="text-gray-400">No active registrations</span>
                            ) : (
                              <div className="space-y-1">
                                {regs.map((reg) => (
                                  <div key={reg.registrationId} className="rounded-md bg-gray-50 px-2 py-1">
                                    <div className="text-[11px] font-medium text-gray-900">
                                      {reg.courseShortName || reg.courseName || 'Course'}
                                    </div>
                                    <div className="text-[11px] text-gray-600">
                                      {reg.batchName || reg.batchInternalName || 'Batch'}
                                    </div>
                                    <div className="text-[10px] text-gray-400">
                                      Reg. No: {reg.registrationNumber}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 align-top">
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-0.5 text-[11px] font-medium text-amber-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              Missing Photo
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-6 text-sm text-gray-600 shadow-sm">
                No students without profile images were found for the current filters.
              </div>
            )}

            {pagination.last_page > 1 && (
              <div className="mt-4 flex items-center justify-between text-xs text-gray-600">
                <div>
                  Page {pagination.current_page} of {pagination.last_page}
                </div>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.current_page <= 1}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pagination.last_page || p + 1, p + 1))}
                    disabled={pagination.current_page >= pagination.last_page}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
